import { Fragment, useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MetricDependency, RasterStats } from "overlay-engine";
import {
  ReportWidget,
  TableHeadingsEditor,
  TooltipBooleanConfigurationOption,
} from "./widgets";
import {
  ReportWidgetTooltipControls,
  TooltipMorePopover,
} from "../../editor/TooltipMenu";
import { useNumberFormatters } from "../hooks/useNumberFormatters";
import { NumberRoundingControl } from "./NumberRoundingControl";
import { MetricLoadingDots } from "../components/MetricLoadingDots";
import { useOverlaySources } from "../hooks/useOverlaySources";
import {
  PaginationFooter,
  PaginationSetting,
  TablePaddingRows,
} from "./Pagination";
import { usePagination } from "../hooks/usePagination";
import {
  ClassTableRow,
  ClassTableRowComponentSettings,
  combineMetricsBySource,
  getClassTableRows,
  hasClassTableRowVisibilityToggle,
  resolveClassTableRowStableId,
  shouldTruncateClassTableRowLabels,
} from "./ClassTableRows";
import {
  classTableRowHasSwatch,
  SwatchForClassTableRow,
} from "./SwatchForClassTableRow";
import { ClassRowSettingsPopover } from "./ClassRowSettingsPopover";
import { LabeledDropdown } from "./LabeledDropdown";
import { VrmSelector } from "./VrmSelector";
import ReportLayerVisibilityCheckbox from "../components/ReportLayerVisibilityCheckbox";
import { LayersIcon } from "@radix-ui/react-icons";
import { usePrimaryGeography } from "../hooks/usePrimaryGeography";
import type { SketchClassPrimaryGeoFields } from "../hooks/usePrimaryGeography";
import { useBaseReportContext } from "../context/BaseReportContext";
import { useSubjectReportContext } from "../context/SubjectReportContext";
import { GeographySelector } from "./InlineMetric";
import * as Tooltip from "@radix-ui/react-tooltip";
import CollectionExpandableName from "./collection/CollectionExpandableName";
import SketchOverlapHint from "./collection/SketchOverlapHint";
import { sketchContributionsForClassTableRow } from "./collection/sketchContributions";
import { useCollectionSketchExpand } from "./collection/useCollectionSketchExpand";
import { ReportUIStateContext } from "../context/ReportUIStateContext";
import { SketchGeometryType } from "../../generated/graphql";

type RasterProportionTableSettings = {
  geographyId?: number | "auto";
  sortBy?: "value" | "name";
  minimumFractionDigits?: number;
  rowsPerPage?: number;
  nameLabel?: string;
  valueLabel?: string;
  sumLabel?: string;
  showSumColumn?: boolean;
  showZeroRows?: boolean;
  hideColorSwatches?: boolean;
} & ClassTableRowComponentSettings;

type ProportionRow = ClassTableRow & {
  sketchSum: number;
  geographySum: number;
};

export const RasterProportionTable: ReportWidget<
  RasterProportionTableSettings
> = ({
  metrics,
  componentSettings,
  sources,
  loading,
  dependencies,
  sketchClass,
  geographies,
}) => {
  const { clippingGeography } = usePrimaryGeography(sketchClass, geographies);
  const { t } = useTranslation("reports");
  const { printing } = useContext(ReportUIStateContext);

  const geographyId: number | undefined =
    componentSettings.geographyId === "auto" ||
    componentSettings.geographyId === undefined
      ? clippingGeography?.id
      : componentSettings.geographyId;

  const sortBy = componentSettings.sortBy || "name";
  const rowsPerPage = componentSettings.rowsPerPage ?? 10;
  const showZeroRows = componentSettings.showZeroRows ?? true;
  const showColorSwatches = !componentSettings.hideColorSwatches;
  const showSumColumn = componentSettings.showSumColumn ?? false;
  const nameLabel = componentSettings.nameLabel || t("Name");
  const valueLabel = componentSettings.valueLabel || t("% Captured");
  const sumLabel = componentSettings.sumLabel || t("Sum");
  const truncateRowLabels = shouldTruncateClassTableRowLabels(componentSettings);

  const formatters = useNumberFormatters({
    minimumFractionDigits: componentSettings.minimumFractionDigits,
  });

  const rows = useMemo<ProportionRow[]>(() => {
    const classRows = getClassTableRows({
      dependencies: dependencies || [],
      sources,
      customLabels: componentSettings.customRowLabels,
      allFeaturesLabel: t("All features"),
      stableIds: componentSettings.rowLinkedStableIds,
      excludedRowKeys: componentSettings.excludedRowKeys,
      includeAllFeaturesRowForGroupedSources:
        componentSettings.includeAllFeaturesRowForGroupedSources,
    });

    if (sources.length === 0 || metrics.length === 0 || loading) {
      return classRows.map((r) => ({
        ...r,
        sketchSum: NaN,
        geographySum: NaN,
      }));
    }

    if (!geographyId) {
      throw new Error("Primary geography not found.");
    }

    const combinedMetrics = combineMetricsBySource<RasterStats>(
      metrics,
      sources,
      geographyId,
      "raster_stats"
    );

    let rows = classRows.map((r) => {
      const combinedForSource = combinedMetrics[r.sourceId];
      const sketchBands = combinedForSource?.fragments?.value?.bands;
      const geographyBands = combinedForSource?.geographies?.value?.bands;
      const sketchSum = sketchBands?.[0]?.sum ?? 0;
      const geographySum = geographyBands?.[0]?.sum ?? 0;
      return {
        ...r,
        sketchSum,
        geographySum,
      };
    });

    if (sortBy === "name") {
      rows = rows.sort((a, b) => a.label.localeCompare(b.label));
    } else {
      rows = rows.sort((a, b) => {
        const aPercent = a.geographySum > 0 ? a.sketchSum / a.geographySum : 0;
        const bPercent = b.geographySum > 0 ? b.sketchSum / b.geographySum : 0;
        return bPercent - aPercent;
      });
    }

    if (!showZeroRows) {
      rows = rows.filter((r) => r.sketchSum > 0);
    }

    return rows;
  }, [
    metrics,
    dependencies,
    sources,
    geographyId,
    componentSettings.customRowLabels,
    componentSettings.rowLinkedStableIds,
    componentSettings.excludedRowKeys,
    componentSettings.includeAllFeaturesRowForGroupedSources,
    showZeroRows,
    sortBy,
    t,
    loading,
  ]);

  const {
    isCollection,
    sketchNameById,
    childSketchIds,
    toggleRow,
    hideCaretExpandTooltip,
    isSketchBreakdownExpanded,
  } = useCollectionSketchExpand(sketchClass, {
    forceAllExpanded: printing,
  });

  const sketchLinesByRowKey = useMemo(() => {
    if (!isCollection || !geographyId || loading) {
      return new Map<
        string,
        ReturnType<typeof sketchContributionsForClassTableRow>
      >();
    }
    const map = new Map<
      string,
      ReturnType<typeof sketchContributionsForClassTableRow>
    >();
    for (const row of rows) {
      const source = sources.find((s) => s.stableId === row.sourceId);
      if (!source) continue;
      map.set(
        row.key,
        sketchContributionsForClassTableRow({
          metrics,
          source,
          geographyId,
          metricType: "raster_stats",
          groupByKey: row.groupByKey,
          childSketchIds,
          geographyDenominator:
            typeof row.geographySum === "number" &&
            Number.isFinite(row.geographySum)
              ? row.geographySum
              : 0,
          sketchNameById,
          t,
        }),
      );
    }
    return map;
  }, [
    isCollection,
    geographyId,
    loading,
    rows,
    metrics,
    sources,
    childSketchIds,
    sketchNameById,
    t,
  ]);

  const hasVisibilityColumn = useMemo(
    () =>
      hasClassTableRowVisibilityToggle(
        rows,
        componentSettings.rowLinkedStableIds
      ),
    [rows, componentSettings.rowLinkedStableIds]
  );

  const {
    currentPage,
    setCurrentPage,
    paginatedItems: paginatedRows,
    paddingRowsCount,
    showPagination,
    totalPages,
    totalRows,
    pageBounds,
  } = usePagination(rows, rowsPerPage);

  const hasSwatchColumn =
    showColorSwatches && rows.some(classTableRowHasSwatch);

  return (
    <Tooltip.Provider delayDuration={400}>
      <div className="mt-3 rounded-md border border-gray-200 shadow-sm w-full max-w-full bg-white overflow-hidden">
      <div className="divide-y divide-gray-100">
        {/* Header row */}
        <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200">
          {hasVisibilityColumn && (
            <div className="flex-none w-6 flex justify-center text-xs text-gray-600 font-semibold uppercase tracking-wide">
              <LayersIcon className="w-4 h-4 text-gray-500" aria-hidden />
            </div>
          )}
          <div className="flex-1 min-w-0 text-gray-600 text-xs font-semibold uppercase tracking-wide">
            {nameLabel}
          </div>
          {showSumColumn && (
            <div className="flex-none text-center text-gray-600 text-xs font-semibold uppercase tracking-wide min-w-[80px]">
              {sumLabel}
            </div>
          )}
          <div className="flex-none text-right text-gray-600 text-xs font-semibold uppercase tracking-wide min-w-[80px]">
            {valueLabel}
          </div>
        </div>
        {paginatedRows.map((row) => {
          const percent =
            !loading && row.geographySum > 0
              ? row.sketchSum / row.geographySum
              : 0;
          const stableId = resolveClassTableRowStableId(
            row,
            componentSettings.rowLinkedStableIds
          );
          const expanded =
            isSketchBreakdownExpanded(row.key);
          const sketchLines = sketchLinesByRowKey.get(row.key) ?? [];
          return (
            <Fragment key={row.key}>
            <div
              className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 ${
                row.sketchSum === 0 ? "opacity-50" : ""
              }`}
            >
              {hasVisibilityColumn && (
                <div className="flex-none w-6 flex justify-center">
                  {stableId ? (
                    <ReportLayerVisibilityCheckbox stableId={stableId} />
                  ) : null}
                </div>
              )}
              {showColorSwatches && <SwatchForClassTableRow row={row} />}
              <div className="flex-1 min-w-0 text-gray-800 text-sm">
                <CollectionExpandableName
                  displayLabel={row.label}
                  truncateRowLabels={truncateRowLabels}
                  expanded={expanded}
                  onToggle={() => toggleRow(row.key)}
                  loading={loading}
                  isCollection={isCollection}
                  caretTooltipEnabled={!hideCaretExpandTooltip}
                  caretTooltipLabel={t("Expand sketch details")}
                  expandAriaLabelExpanded={t(
                    "Collapse sketch breakdown for {{name}}",
                    { name: row.label },
                  )}
                  expandAriaLabelCollapsed={t(
                    "Expand sketch breakdown for {{name}}",
                    { name: row.label },
                  )}
                />
              </div>
              {showSumColumn && (
                <div className="flex-none text-center text-gray-900 tabular-nums text-sm min-w-[80px]">
                  {loading ? (
                    <MetricLoadingDots />
                  ) : (
                    formatters.decimal(row.sketchSum)
                  )}
                </div>
              )}
              <div className="flex-none text-right text-gray-900 tabular-nums text-sm min-w-[80px]">
                {loading ? <MetricLoadingDots /> : formatters.percent(percent)}
              </div>
            </div>
            {isCollection && expanded && sketchLines.length === 0 && (
              <div className="flex flex-wrap items-center gap-3 border-t border-slate-200/80 bg-slate-100 px-3 py-2.5 text-sm italic text-gray-600">
                <div className="flex-none w-6" aria-hidden />
                {hasSwatchColumn && (
                  <div className="flex-none w-4" aria-hidden />
                )}
                <div className="min-w-0 flex-1">
                  {t(
                    "No individual sketches contributed to this category.",
                  )}
                </div>
              </div>
            )}
            {isCollection &&
              expanded &&
              sketchLines.map((sk) => (
                <div
                  key={`${row.key}-sketch-${sk.sketchId}`}
                  className={`flex flex-wrap items-center gap-3 border-t border-slate-200/80 bg-slate-100 px-3 py-2 hover:bg-slate-200/30 ${
                    row.sketchSum === 0 ? "opacity-50" : ""
                  }`}
                >
                  {hasVisibilityColumn && (
                    <div className="flex-none w-6" aria-hidden />
                  )}
                  {hasSwatchColumn && (
                    <div className="flex-none w-4 flex justify-center" aria-hidden />
                  )}
                  <div className="flex min-w-0 flex-1 items-center gap-1 text-sm text-gray-800">
                    <span className="min-w-0">{sk.sketchName}</span>
                    <SketchOverlapHint
                      hasOverlap={sk.hasOverlap}
                      sketchDisplayName={sk.sketchName}
                      overlapPartnerSketchNames={
                        sk.overlapPartnerSketchNames
                      }
                    />
                  </div>
                  {showSumColumn && (
                    <div className="flex-none text-center text-gray-900 tabular-nums text-sm min-w-[80px]">
                      {loading ? (
                        <MetricLoadingDots />
                      ) : (
                        formatters.decimal(sk.primaryValue)
                      )}
                    </div>
                  )}
                  <div className="flex-none text-right text-gray-900 tabular-nums text-sm min-w-[80px]">
                    {loading ? (
                      <MetricLoadingDots />
                    ) : (
                      formatters.percent(sk.fractionOfGeography)
                    )}
                  </div>
                </div>
              ))}
            </Fragment>
          );
        })}
        <TablePaddingRows
          count={paddingRowsCount}
          includeVisibilityColumn={hasVisibilityColumn}
          includeColorColumn={
            showColorSwatches && rows.some(classTableRowHasSwatch)
          }
          showPercentColumn={showSumColumn}
          numericAlign={showSumColumn ? "center" : "right"}
        />
      </div>
      {!loading && rows.length === 0 && (
        <div className="mt-3 border border-black/10 rounded bg-gray-50 px-3 py-2 text-gray-600 text-sm mx-3 mb-3">
          <Trans ns="reports">No data available.</Trans>
        </div>
      )}
      {showPagination && (
        <PaginationFooter
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          pageBounds={pageBounds}
          onPageChange={setCurrentPage}
        />
      )}
      </div>
    </Tooltip.Provider>
  );
};

export const RasterProportionTableTooltipControls: ReportWidgetTooltipControls =
  ({
    node,
    onUpdate,
    onUpdateDependencyParameters,
    onUpdateAllDependencies,
  }) => {
    const { t } = useTranslation("admin:reports");
    const dependencies = useMemo(
      () => (node.attrs?.metrics || []) as MetricDependency[],
      [node.attrs?.metrics]
    );
    const settings: RasterProportionTableSettings = useMemo(
      () => node.attrs?.componentSettings || {},
      [node.attrs?.componentSettings]
    );

    const sortBy = settings.sortBy || "name";
    const showZeroRows = settings.showZeroRows ?? true;
    const showSumColumn = settings.showSumColumn ?? false;
    const rowsPerPage = settings.rowsPerPage ?? 10;

    const headingsLabelKeys = useMemo(
      (): string[] =>
        showSumColumn
          ? ["nameLabel", "valueLabel", "sumLabel"]
          : ["nameLabel", "valueLabel"],
      [showSumColumn]
    );
    const headingsLabelDisplayNames = useMemo(
      (): string[] =>
        showSumColumn ? ["Name", "% Captured", "Sum"] : ["Name", "% Captured"],
      [showSumColumn]
    );

    const { filteredSources: sources } = useOverlaySources(dependencies);

    const { geographies } = useBaseReportContext();
    const subjectReportContext = useSubjectReportContext();
    const sketchClass = subjectReportContext.data?.sketch?.sketchClass;
    const sketchClassForPrimaryGeography: SketchClassPrimaryGeoFields =
      sketchClass ?? {
        geometryType: SketchGeometryType.Polygon,
        clippingGeographies: [],
        validChildren: [],
        project: { sketchClasses: [] },
      };
    const { clippingGeography } = usePrimaryGeography(
      sketchClassForPrimaryGeography,
      geographies
    );

    const handleUpdate = (patch: Partial<RasterProportionTableSettings>) => {
      onUpdate({
        componentSettings: {
          ...settings,
          ...patch,
        },
      });
    };

    const currentVrm = useMemo(() => {
      const fragmentDep = dependencies.find(
        (d) => d.subjectType === "fragments"
      );
      return fragmentDep?.parameters?.vrm;
    }, [dependencies]);

    const currentGeographyVrm = useMemo(() => {
      const geographyDep = dependencies.find(
        (d) => d.subjectType === "geographies"
      );
      return geographyDep?.parameters?.vrm;
    }, [dependencies]);

    const handleVrmChange = (next: false | "auto" | number | undefined) => {
      onUpdateDependencyParameters((dependency) => {
        const params = { ...(dependency.parameters || {}) };
        if (dependency.subjectType !== "fragments") {
          return params;
        }
        if (next === undefined) {
          delete params.vrm;
        } else {
          params.vrm = next;
        }
        return params;
      });
    };

    const handleGeographyVrmChange = (
      next: false | "auto" | number | undefined
    ) => {
      onUpdateDependencyParameters((dependency) => {
        const params = { ...(dependency.parameters || {}) };
        if (dependency.subjectType !== "geographies") {
          return params;
        }
        if (next === undefined) {
          delete params.vrm;
        } else {
          params.vrm = next;
        }
        return params;
      });
    };

    const sortOptions = [
      { value: "name", label: t("Name") },
      { value: "value", label: t("% Captured") },
    ];

    return (
      <div className="flex gap-3 items-center text-sm text-gray-800">
        <GeographySelector
          geographies={geographies}
          clippingGeography={clippingGeography}
          value={settings.geographyId}
          onChange={(geographyId) => handleUpdate({ geographyId })}
          t={t}
        />
        <NumberRoundingControl
          value={settings.minimumFractionDigits}
          onChange={(minimumFractionDigits) =>
            handleUpdate({ minimumFractionDigits })
          }
        />
        <LabeledDropdown
          label={t("Sort by")}
          value={sortBy}
          options={sortOptions}
          onChange={(val) => handleUpdate({ sortBy: val as "value" | "name" })}
        />
        <ClassRowSettingsPopover
          settings={settings}
          onUpdateSettings={(patch) => handleUpdate(patch)}
          dependencies={dependencies || []}
          sources={sources}
          onUpdateDependencyParameters={onUpdateDependencyParameters}
          onUpdateAllDependencies={onUpdateAllDependencies}
          t={t}
          allowedGeometryTypes={["SingleBandRaster"]}
          hideGroupBy={true}
          showZeros={showZeroRows}
          onShowZerosChange={(next) => handleUpdate({ showZeroRows: next })}
          showColorSwatches={!settings.hideColorSwatches}
          onShowColorSwatchesChange={(next) =>
            handleUpdate({ hideColorSwatches: next ? undefined : true })
          }
        />
        <TableHeadingsEditor
          labelKeys={headingsLabelKeys}
          labelDisplayNames={headingsLabelDisplayNames}
          componentSettings={settings}
          onUpdate={onUpdate}
        />
        <TooltipBooleanConfigurationOption
          label={t("show sum")}
          checked={showSumColumn}
          onChange={(next) => handleUpdate({ showSumColumn: next })}
        />
        <TooltipMorePopover>
          <PaginationSetting
            rowsPerPage={rowsPerPage}
            onChange={(next: number) => handleUpdate({ rowsPerPage: next })}
          />
          <VrmSelector
            label={t("Sketch VRM")}
            value={currentVrm}
            onChange={handleVrmChange}
          />
          <VrmSelector
            geography
            value={currentGeographyVrm}
            onChange={handleGeographyVrmChange}
          />
          <TooltipBooleanConfigurationOption
            label={t("Truncate row labels")}
            checked={shouldTruncateClassTableRowLabels(settings)}
            checkboxFirst
            onChange={(next) =>
              handleUpdate({
                disableRowLabelTruncation: next ? undefined : true,
              })
            }
          />
        </TooltipMorePopover>
      </div>
    );
  };
